import * as React from "react";

import { cn } from "@/lib/utils";

import { acceptInvitationSignUp } from "@/api/invitations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import FormTemplate, { FormSectionTemplate } from "../form-template";
import { toast } from "../ui/use-toast";
import { useAppContext } from "@/providers/app-context";
import { useNavigate } from "react-router-dom";
import { isApiError } from "@/api/_client";

const formSchema = z.object({
  first_name: z.string().min(0, "Required"),
  last_name: z.string().min(0, "Required"),
  password: z.string().min(5),
});

interface UserSignUpFormProps extends React.HTMLAttributes<HTMLDivElement> {
  invitationToken: string;
}

export function UserSignUpForm({ className, ...props }: UserSignUpFormProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { useConfirmation } = useAppContext();
  const confirmation = useConfirmation();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    // defaultValues: defaultValues,
    mode: "onSubmit",
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true);
    const res = await acceptInvitationSignUp({
      invitationToken: props.invitationToken,
      data: data,
    });
    if (isApiError(res)) {
      if (res.formErrors) {
        for (const [key, value] of Object.entries(res.formErrors)) {
          form.setError(key as keyof typeof formSchema._type, {
            type: "manual",
            message: value as string,
          });
        }
      } else {
        toast({
          title: "Error",
          description: res.error,
        });
      }
    } else {
      confirmation.open({
        title: "Success!",
        description: `You should be receiving an email from us to soon to confirm your account.`,
        confirmText: "Go to sign in page",
        hideCancel: true,
        callback: async () => {
          navigate("/");
        },
      });
    }
    setIsLoading(false);
  }

  const signUpFormTemplate = (): FormSectionTemplate[] => [
    {
      fields: [
        {
          name: "first_name",
          showTopLabel: true,
          label: "First name",
          placeholder: "Enter your first name",
          type: "text",
        },
        {
          name: "last_name",
          showTopLabel: true,
          label: "Last name",
          placeholder: "Enter your last name",
          type: "text",
        },
        {
          name: "password",
          label: "Password",
          showTopLabel: true,
          type: "password",
          placeholder: "Enter your password",
        },
      ],
    },
  ];

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <FormTemplate
        form={form}
        sections={signUpFormTemplate()}
        onSubmit={onSubmit}
        isLoading={isLoading}
        submitLabel="Sign up"
      />
    </div>
  );
}
